import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { myOngoing, getMe } from "../lib/api";
import { openChatStream } from "../lib/ws";

const API_BASE = process.env.REACT_APP_API_BASE ?? "/api";

function avatarSrc(url, token) {
  if (!url) return null;
  const full = url.startsWith("http") ? url : `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
  const join = full.includes("?") ? "&" : "?";
  return `${full}${join}t=${encodeURIComponent(token)}`;
}

function Avatar({ url, token }) {
  const src = avatarSrc(url, token);
  if (!src) return <div className="w-5 h-5 rounded-full bg-neutral-200" />;
  return <img src={src} alt="" className="w-5 h-5 rounded-full object-cover" />;
}

function prettySystem(m) {
  if (m.event === "user_finished" && m.username) return `${m.username} se ha levantado.`;
  if (m.event === "user_joined" && m.username) return `${m.username} entró.`;
  if (m.event === "user_left" && m.username) return `${m.username} salió.`;
  return null;
}

function withTimeout(ms) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  return {
    signal: ctrl.signal,
    clear: () => clearTimeout(t)
  };
}

export default function Activity() {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("fw_token") || "", []);
  const [me, setMe] = useState(null);
  const [ongoing, setOngoing] = useState(null);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatOnline, setChatOnline] = useState(true);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const meResp = await getMe();
        if (!alive) return;
        setMe(meResp);
      } catch {}

      try {
        const mo = await myOngoing();
        if (!alive) return;
        if (mo && mo.id) {
          setOngoing(mo);
        } else {
          setOngoing(null);
        }
      } catch {}

      try {
        const t = withTimeout(3000);
        const r = await fetch(`${API_BASE}/chat/session`, { headers: { "X-User-Token": token }, signal: t.signal });
        t.clear();
        if (!alive) return;
        if (r.ok) {
          const j = await r.json();
          setChatSessionId(j.chatSessionId || null);
          setChatOnline(!!j.chatSessionId);
        } else {
          setChatOnline(false);
        }
      } catch {
        setChatOnline(false);
      }
    };

    load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token]);

  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef(null);

  const pendingByClientId = useRef(new Map());
  const seenIds = useRef(new Set());
  const recentlySent = useRef([]);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  };

  useEffect(() => {
    if (!chatSessionId || !token) return;
    const stop = openChatStream(
      API_BASE,
      chatSessionId,
      token,
      (init) => {
        const next = [];
        const ids = seenIds.current;
        for (const m of Array.isArray(init) ? init : []) {
          const id = m.id || null;
          if (id && ids.has(id)) continue;
          if (id) ids.add(id);
          next.push(m);
        }
        setMsgs(next);
        scrollToBottom();
      },
      (m) => {
        const id = m.id || null;
        if (id && seenIds.current.has(id)) return;

        const cid = m.clientId || null;
        if (cid && pendingByClientId.current.has(cid)) {
          const localId = pendingByClientId.current.get(cid);
          pendingByClientId.current.delete(cid);
          setMsgs(prev => {
            const idx = prev.findIndex(x => x.id === localId);
            if (idx >= 0) {
              const copy = prev.slice();
              copy[idx] = m;
              if (id) seenIds.current.add(id);
              return copy;
            }
            if (id) seenIds.current.add(id);
            return prev.concat(m);
          });
          scrollToBottom();
          return;
        }

        const now = Date.now();
        while (recentlySent.current.length && now - recentlySent.current[0].atMs > 3500) {
          recentlySent.current.shift();
        }
        const mineEcho =
          me &&
          m.username === me.username &&
          m.type === "text" &&
          recentlySent.current.some(r => r.text === (m.text || "") && now - r.atMs < 3500);

        if (mineEcho) {
          if (id) seenIds.current.add(id);
          return;
        }

        setMsgs(prev => {
          if (id) seenIds.current.add(id);
          return prev.concat(m);
        });
        scrollToBottom();
      }
    );
    return () => { try { stop && stop(); } catch {} };
  }, [chatSessionId, token, me]);

  const send = useCallback(async () => {
    const t = String(text || "").trim();
    if (!t || !me) return;
    const clientId = `c_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const optimistic = {
      id: `local-${clientId}`,
      type: "text",
      event: "user_message",
      ts: new Date().toISOString(),
      userId: me.id,
      username: me.username,
      pfpUrl: me.pfpUrl,
      text: t,
      clientId
    };
    pendingByClientId.current.set(clientId, optimistic.id);
    recentlySent.current.push({ text: t, atMs: Date.now() });
    setMsgs(prev => prev.concat(optimistic));
    setText("");
    scrollToBottom();
    try {
      await fetch(`${API_BASE}/chat/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-Token": token },
        body: JSON.stringify({ text: t, clientId })
      });
    } catch {}
  }, [text, token, me]);

  const onKey = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }, [send]);

  const goFinalize = useCallback(() => {
    if (!ongoing?.id) return;
    navigate("/activity/finalizar");
  }, [ongoing, navigate]);

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      <div className="flex-1 min-h-0 px-4 pt-4 pb-[92px]">
        {ongoing ? (
          chatOnline && chatSessionId ? (
            <div className="flex flex-col h-full">
              <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-2 border border-neutral-200 rounded-2xl bg-white">
                {msgs.map((m, i) => {
                  if (m.type === "system") {
                    const txt = prettySystem(m);
                    return txt ? <div key={m.id || i} className="text-[11px] text-neutral-500 text-center">{txt}</div> : null;
                  }
                  const mine = me && (m.userId === me.id || m.username === me.username);
                  return (
                    <div key={m.id || i} className={`flex items-end ${mine ? "justify-end" : "justify-start"} gap-2`}>
                      {!mine ? <Avatar url={m.pfpUrl} token={token} /> : null}
                      <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm leading-snug ${mine ? "bg-black text-white" : "bg-neutral-100 text-neutral-900"}`} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {String(m.text || "")}
                      </div>
                      {mine ? <Avatar url={m.pfpUrl} token={token} /> : null}
                    </div>
                  );
                })}
              </div>
              <div className="pt-2">
                <div className="flex items-center gap-2">
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={onKey}
                    className="flex-1 resize-none rounded-xl border border-neutral-300 px-3 py-2 text-sm focus:outline-none"
                    rows={1}
                    placeholder="di algo…"
                  />
                  <button onClick={send} className="rounded-xl bg-black text-white px-4 py-2 text-sm">Enviar</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full border border-dashed border-neutral-300 rounded-2xl flex items-center justify-center text-neutral-500">
              chat offline
            </div>
          )
        ) : (
          <div className="h-full border border-dashed border-neutral-300 rounded-2xl flex items-center justify-center text-neutral-500">
            empieza una actividad para abrir el chat
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-neutral-200 px-4 py-3">
        <div className="max-w-[540px] mx-auto">
          <button
            onClick={goFinalize}
            disabled={!ongoing}
            className={`w-full rounded-2xl px-4 py-4 text-lg ${ongoing ? "bg-black text-white" : "bg-neutral-300 text-neutral-600"}`}
          >
            Finalizar
          </button>
        </div>
      </div>
    </div>
  );
}
